import React, { useCallback, useEffect, useMemo, useState } from "react";
import { pagosApi } from "./pagosApi";

export const ESTADO_LABELS = {
  PENDIENTE: "Pendiente",
  EN_ARCHIVO_BANCARIO_SIN_CONFIRMAR: "En archivo bancario · sin confirmar",
  PAGO_MANUAL_INFORMADO: "Pago manual informado · sin confirmar",
  CONFIRMADA: "Confirmada con evidencia bancaria",
};

const FILTROS = [
  { key: "TODAS", label: "Todas" },
  { key: "PENDIENTE", label: "Pendientes" },
  { key: "EN_ARCHIVO_BANCARIO_SIN_CONFIRMAR", label: "En archivo sin confirmar" },
  { key: "PAGO_MANUAL_INFORMADO", label: "Pago manual informado" },
];

const money = new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", minimumFractionDigits: 2 });
const formatMoney = (value) => (typeof value === "number" && Number.isFinite(value) ? money.format(value) : "No disponible");
const formatDate = (value) => {
  const [year, month, day] = String(value || "").slice(0, 10).split("-");
  return year && month && day ? `${day}/${month}/${year}` : "Sin fecha";
};

function InformarPagoManual({ onSubmit, onCancel }) {
  const [form, setForm] = useState({ informadoPor: "", motivo: "", fechaPagoInformada: "" });
  const change = (field) => (event) => setForm({ ...form, [field]: event.target.value });
  const valid = form.informadoPor.trim() && form.motivo.trim();
  return (
    <div className="pagos-manual-form">
      <label><span>Informado por</span><input aria-label="Informado por" value={form.informadoPor} onChange={change("informadoPor")} /></label>
      <label><span>Motivo</span><input aria-label="Motivo" value={form.motivo} onChange={change("motivo")} /></label>
      <label><span>Fecha informada del pago</span><input aria-label="Fecha informada del pago" type="date" value={form.fechaPagoInformada} onChange={change("fechaPagoInformada")} /></label>
      <small>Queda INFORMADO. La confirmación requiere evidencia bancaria y no está habilitada en esta fase.</small>
      <div>
        <button type="button" className="pagos-primary" disabled={!valid}
          onClick={() => onSubmit({ informadoPor: form.informadoPor.trim(), motivo: form.motivo.trim(), fechaPagoInformada: form.fechaPagoInformada || null })}>Registrar pago informado</button>
        <button type="button" className="pagos-secondary" onClick={onCancel}>Cancelar</button>
      </div>
    </div>
  );
}

export default function LiquidacionesRegistradas({ token, persistencia, refreshKey }) {
  const [obligaciones, setObligaciones] = useState([]);
  const [filtro, setFiltro] = useState("TODAS");
  const [editando, setEditando] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");
  const habilitada = Boolean(persistencia?.habilitada);

  const cargar = useCallback(async () => {
    if (!habilitada) return;
    setLoading(true); setError("");
    try {
      setObligaciones(await pagosApi.obtenerPendientes(token) || []);
    } catch (requestError) {
      setError(requestError?.message || "No se pudieron consultar las obligaciones registradas.");
    } finally {
      setLoading(false);
    }
  }, [habilitada, token]);

  useEffect(() => { cargar(); }, [cargar, refreshKey]);

  const visibles = useMemo(() => (filtro === "TODAS" ? obligaciones : obligaciones.filter((item) => item.estado === filtro)), [obligaciones, filtro]);
  const total = useMemo(() => visibles.reduce((sum, item) => sum + (typeof item.importe === "number" ? item.importe : 0), 0), [visibles]);

  const informar = async (id, body) => {
    setError(""); setAviso("");
    try {
      const response = await pagosApi.informarPagoManual(id, body, token);
      setEditando("");
      setAviso(response?.alertaDoblePago
        ? "Pago manual informado con ALERTA: la obligación figura en un archivo bancario. Posible doble pago."
        : response?.creado === false ? "La obligación ya tenía un pago manual informado; no se duplicó." : "Pago manual informado. Sigue sin confirmación bancaria.");
      await cargar();
    } catch (requestError) {
      setError(requestError?.message || "No se pudo informar el pago manual.");
    }
  };

  return (
    <section className="pagos-panel" aria-labelledby="pagos-liquidaciones-title" data-testid="liquidaciones-registradas">
      <div className="pagos-section-heading">
        <div>
          <h2 id="pagos-liquidaciones-title">Obligaciones sin confirmación bancaria</h2>
          <p>Lo registrado desde Controles previos: lo excluido sigue pendiente hasta que haya evidencia bancaria.</p>
        </div>
        <span className="pagos-mode-badge">{habilitada ? `Desarrollo aislado · ${persistencia.baseDatos}` : "Persistencia no habilitada"}</span>
      </div>

      {!habilitada ? (
        <div className="pagos-alert" role="status">{persistencia?.motivo || "La persistencia de Pagos no está disponible en este entorno."}</div>
      ) : <>
        <div className="pagos-channel pagos-filters" role="group" aria-label="Filtrar obligaciones"><div>
          {FILTROS.map(({ key, label }) => (
            <button key={key} type="button" className={filtro === key ? "is-active" : ""} aria-pressed={filtro === key} onClick={() => setFiltro(key)}>
              {label} ({key === "TODAS" ? obligaciones.length : obligaciones.filter((item) => item.estado === key).length})
            </button>
          ))}
        </div></div>
        <p className="pagos-cell-note">{visibles.length} obligaciones · {formatMoney(total)} · ninguna confirmada</p>
        {aviso && <div className="pagos-alert pagos-alert--info" role="status">{aviso}</div>}
        {error && <div className="pagos-alert pagos-alert--error" role="alert">{error}</div>}
        {loading ? <div className="pagos-empty"><p>Consultando obligaciones…</p></div> : (
          <div className="pagos-table-wrap"><table><thead><tr>
            <th>Fecha de pago</th><th>Comercio</th><th>Cuenta</th><th>Importe</th><th>Estado</th><th>Pago manual</th>
          </tr></thead><tbody>
            {!visibles.length ? <tr><td colSpan="6"><div className="pagos-empty"><p>No hay obligaciones registradas para este filtro.</p></div></td></tr> : visibles.map((item) => (
              <tr key={item.id} className={item.alertaDoblePago ? "pagos-row--review" : ""}>
                <td>{formatDate(item.fechaPago)}</td>
                <td><strong>{item.nombreComercio}</strong><small className="pagos-cell-note">{item.razonSocial} · CUIT {item.cuitEnmascarado}</small></td>
                <td>{item.cuentaEnmascarada}</td>
                <td>{formatMoney(item.importe)}</td>
                <td><span className={`pagos-status ${item.estado === "PENDIENTE" ? "pagos-status--warning" : "pagos-status--pending"}`}>{ESTADO_LABELS[item.estado] || item.estado}</span></td>
                <td>{item.pagoManualEstado ? <>
                  <span className="pagos-status pagos-status--pending">{item.pagoManualEstado} · {item.pagoManualInformadoPor}</span>
                  {item.alertaDoblePago && <small className="pagos-cell-note">Alerta: también figura en archivo bancario</small>}
                </> : editando === item.id
                  ? <InformarPagoManual onSubmit={(body) => informar(item.id, body)} onCancel={() => setEditando("")} />
                  : <button type="button" className="pagos-secondary" onClick={() => setEditando(item.id)}>Informar pago manual</button>}
                </td>
              </tr>
            ))}
          </tbody></table></div>
        )}
      </>}
    </section>
  );
}
